import { RoundedBox } from '@react-three/drei'
import { Shape } from 'three'
import { Helper } from '../../../../../classes/helper'
import { useMeasures } from '../../../../../hooks/state/useMeasures'
import { useRoof } from '../../../../../hooks/state/useRoof'
import * as THREE from 'three'
import { CachedMeshPhysicalMaterial } from '../../../../material/cachedMeshPhysicalMaterial'

interface Props {
  size: {
    length: number
    width: number
    offset: number
    barDepth: number
  }
}

export const RoofBarOuter = ({ size }: Props): JSX.Element => {
  const {
    userWidth,
    verandaTyp,
    userAngle,
    isQubus,
    coverStripSize,
    hasCoverStrips,
    schwelleSize,
  } = useMeasures()

  const { color, roofGlassDepth } = useRoof()

  const positionOuter =
    userWidth! / 2 - (isQubus ? verandaTyp.log.width / 2 : 0)

  const shape = new Shape()
  shape.moveTo(0, 0)
  shape.lineTo(size.offset, size.width)
  shape.lineTo(size.length + size.offset, size.width)
  shape.lineTo(size.length, 0)
  shape.lineTo(0, 0)
  const extrudeSettings = {
    curveSegments: 1,
    steps: 1,
    depth: size.barDepth,
    bevelEnabled: false,
  }

  const roofTopBarSize = {
    length: size.length + schwelleSize.depth,
    width: verandaTyp.roofBar.width,

    barDepth: verandaTyp.roofBar.depth,
    rad: (userAngle * Math.PI) / 180,
    offset: Math.min(
      Math.tan((userAngle * Math.PI) / 180) *
        (hasCoverStrips ? coverStripSize.y : verandaTyp.roofBar.width),
      length / 2 + 1
    ),
    offset1: Math.min(
      Math.tan((userAngle * Math.PI) / 180) * verandaTyp.roofBar.width,
      length / 2
    ),
  }

  const shapeCoverStrip = new Shape()
  shapeCoverStrip.moveTo(0, 0)
  shapeCoverStrip.lineTo(
    roofTopBarSize.offset,
    coverStripSize.y + roofGlassDepth
  )
  shapeCoverStrip.lineTo(
    roofTopBarSize.length + roofTopBarSize.offset,
    coverStripSize.y + roofGlassDepth
  )
  shapeCoverStrip.lineTo(roofTopBarSize.length, 0)
  shapeCoverStrip.lineTo(0, 0)
  const extrudeCoverStripSettings = {
    curveSegments: 1,
    steps: 1,
    depth: coverStripSize.x,
    bevelEnabled: false,
  }

  return (
    <>
      {[...Array(isQubus && !roofGlassDepth ? 0 : 2)].map((_, index) => {
        return (
          <group
            position={[index ? positionOuter * -1 : positionOuter, 0, 0]}
            key={`roofBarOuter${index}`}
          >
            <mesh
              rotation={[0, Helper.convertAngleToRadiant(90), 0]}
              position={[index ? 0 : -size.barDepth, 0, size.length]}
              castShadow={true}
            >
              <extrudeGeometry
                attach="geometry"
                args={[shape, extrudeSettings]}
              />

              <CachedMeshPhysicalMaterial color={color} />
            </mesh>
            <group position={[0, 0, 0]} castShadow={true}>
              {hasCoverStrips ? (
                <mesh
                  position={[
                    index ? 0 : -coverStripSize.x,
                    verandaTyp.roofBar.width,
                    roofTopBarSize.length,
                  ]}
                  rotation={[0, Helper.convertAngleToRadiant(90), 0]}
                >
                  <extrudeGeometry
                    attach="geometry"
                    args={[shapeCoverStrip, extrudeCoverStripSettings]}
                  />
                  <CachedMeshPhysicalMaterial color={color} opacity={1} />
                </mesh>
              ) : (
                <RoundedBox
                  position={[
                    index ? coverStripSize.x / 2 : -coverStripSize.x / 2,
                    verandaTyp.roofBar.width + coverStripSize.y / 2,
                    (size.length + schwelleSize.depth) / 2 - size.offset,
                  ]}
                  args={[
                    coverStripSize.x,
                    coverStripSize.y,
                    size.length + schwelleSize.depth,
                  ]}
                  smoothness={0.5}
                  radius={0.0025}
                  matrixWorldAutoUpdate={undefined}
                  getObjectsByProperty={undefined}
                  getVertexPosition={undefined}
                >
                  <CachedMeshPhysicalMaterial color={color} />
                </RoundedBox>
              )}
            </group>
          </group>
        )
      })}
    </>
  )
}
